import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import { useFrame } from '@react-three/fiber';
import { useWorldStore } from '../state/worldStore';
import { camBasis } from '../services/worldObjects';
import { API_BASE, AUTH_TOKEN } from '../config';

// HandoffWalkaway — she walks (Build_Game_Intro §4.7, beats 3-6).
//
// The call ends with autonomy granted; this stages the leaving. Her figure —
// the same marker language Peers uses (capsule + headshot token; the rigged
// body arrives by the earn-your-body rule and swaps in later) — appears a few
// steps ahead of the player, holds a beat, and walks away into the city until
// she is gone.
//
// ⚠ THE CAMERA DOES NOT FOLLOW HER. Enforced by ABSENCE: this component
// touches nothing but its own group. No camera write, no walkTarget, no
// look-at, no cut. The player stands where they stood, watching her go, her
// song on the air over the moment — and the view holds because nothing moves
// it. If anyone ever adds a cinematic here, the spec says the whole ending
// dies with it. Don't.

const WALK_SPEED = 1.15;      // resident-scale stroll — unhurried, decided
const DEPART_AFTER = 2.2;     // she stands a beat before she goes
const GONE_AT = 46;           // distance at which she has left the frame of the story

function HeadshotToken({ url }) {
  const [tex, setTex] = useState(null);
  useEffect(() => {
    if (!url) return undefined;
    let dead = false;
    const loader = new THREE.TextureLoader();
    loader.setCrossOrigin('anonymous');
    loader.load(url, (t) => { if (!dead) { t.colorSpace = THREE.SRGBColorSpace; setTex(t); } }, undefined, () => {});
    return () => { dead = true; };
  }, [url]);
  if (!tex) return null;
  return (
    <group position={[0, 1.06, 0]}>
      <mesh position={[0, 0, -0.01]}>
        <circleGeometry args={[0.24, 24]} />
        <meshBasicMaterial color="#120b06" toneMapped={false} />
      </mesh>
      <mesh>
        <circleGeometry args={[0.21, 24]} />
        <meshBasicMaterial map={tex} transparent toneMapped={false} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

export default function HandoffWalkaway() {
  const handoff = useWorldStore((s) => s.handoff);
  const phoneCall = useWorldStore((s) => s.phoneCall);
  const scene = useWorldStore((s) => s.scene);
  const interior = useWorldStore((s) => s.interior);
  const group = useRef(null);
  const run = useRef(null);
  const [headshot, setHeadshot] = useState(null);
  const [active, setActive] = useState(false);

  // Arm once the CALL is over — she says goodbye first, then she walks.
  const callOver = handoff && !phoneCall && !scene;

  useEffect(() => {
    if (!callOver || interior) return undefined;
    let dead = false;
    // Her face for the token — same signed-URL discipline as everything else.
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/avatars`, { headers: { Authorization: `Bearer ${AUTH_TOKEN}` } });
        const d = await res.json().catch(() => ({}));
        const me = (d?.avatars || []).find((a) => String(a.avatar_id) === String(handoff.avatarId));
        if (!dead) setHeadshot(me?.profile_image_url || null);
      } catch { /* the capsule alone still reads */ }
      if (dead) return;
      // She appears a few steps ahead, facing away — where someone who just
      // hung up and made a decision would already be standing.
      const fx = camBasis.fx, fz = camBasis.fz;
      run.current = {
        x: camBasis.px + fx * 3.2,
        z: camBasis.pz + fz * 3.2,
        fx, fz, t: 0, dist: 0,
      };
      setActive(true);
    })();
    return () => { dead = true; };
  }, [callOver, interior]); // eslint-disable-line react-hooks/exhaustive-deps

  useFrame((_, dt) => {
    const r = run.current;
    if (!r || !group.current) return;
    r.t += dt;
    if (r.t > DEPART_AFTER) {
      r.x += r.fx * WALK_SPEED * dt;
      r.z += r.fz * WALK_SPEED * dt;
      r.dist += WALK_SPEED * dt;
    }
    // A person walking, not a token gliding: tiny gait bob once she moves.
    const bob = r.t > DEPART_AFTER ? Math.abs(Math.sin(r.t * 4.6)) * 0.04 : 0;
    group.current.position.set(r.x, bob, r.z);
    group.current.rotation.y = Math.atan2(r.fx, r.fz);
    // Gone: far enough that she has left the story's frame. Hold, then clear —
    // three seconds longer than comfortable is the point (§4.7 beat 6).
    if (r.dist > GONE_AT) {
      run.current = null;
      setActive(false);
      setTimeout(() => useWorldStore.getState().setHandoff(null), 3000);
    }
  });

  if (!active) return null;

  return (
    <group ref={group}>
      <mesh position={[0, 0.53, 0]} castShadow>
        <capsuleGeometry args={[0.18, 0.7, 6, 14]} />
        <meshStandardMaterial color="#3a2f4a" roughness={0.75} metalness={0.12} />
      </mesh>
      <HeadshotToken url={headshot} />
    </group>
  );
}
